/**
 * LightSportsPage.jsx
 * Premium light-mode sports betting page — standalone UI
 * Replicates the Figma design with two-panel layout:
 *   Left:  match list with league headers + odds
 *   Right: match detail with stats card, win-rate bar, and market odds
 */
import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import SportsbookMobilePortal from './SportsbookMobilePortal';
import './InternationalPage.css';
import { SportsBetCartContext } from '../../../_context/SportsBetCartContext';
import { SportsnewContext } from '../../../_context/SportsnewContext';
import { SPORTSNEW_LIST_STATUS } from '../../../_context/_helpers/sportsnewListRequestState';
import { sortMarketRow, deriveGroupKey, oddRowLineLabel, groupMarketsByType } from '../_helpers/marketRowSorting';
import { buildSportsnewMarketTabs, ensureSportsnewMarketTab, marketMatchesSportsnewCategory } from '../_helpers/sportsnewMarketCategories';
import { isOddLocked } from '../../../_context/_helpers/sportsnewListPatch';
import { marketIdentityKey } from '../../../_context/_helpers/sportsnewDetailPatch';
import { mainOddsResultLabel } from '../_helpers/mainOddsPolicy';
import { countryFlagEmoji, resolveSportsnewCountryName } from '../_helpers/countryFlags';
import { formatSportsMatchTime, hasSportsMatchStartElapsed } from '../_helpers/sportsMatchTime';
import { getSportsnewSportLabel } from '../_helpers/liveDisplay';
import { sportsnewSelectionIdentity } from '../_helpers/sportsnewSelectionIdentity';
import getSelectionLabel from './selectionLabelResolver';

// Slider images
import slide1 from '../../../assets/Slider/slide1.png';
import slide2 from '../../../assets/Slider/slide2.png';
import slide3 from '../../../assets/Slider/slide3.png';
import dressImage1 from '../../../assets/away_team_red.png';
import dressImage2 from '../../../assets/away_team_blue.png';
import dressRedImage from '../../../assets/away_team_red.png';
import dressBlueImage from '../../../assets/away_team_blue.png';

// SVG icons for sport tabs — limited to sports the /sportsnew API actually supports
// (see SPORTSNEW_SPORTS in SportsnewContext.jsx / docs/sport_socket_guide.md)
import soccerIcon from '../../../assets/Games/1.svg';
import basketballIcon from '../../../assets/Games/2.svg';
import baseballIcon from '../../../assets/Games/3.svg';
import hockeyIcon from '../../../assets/Games/4.svg';
import volleyballIcon from './assets/valleyball.png';
import { ChartNoAxesColumn, ArrowDownUp, ArrowUpDown, ChevronDown, RefreshCw, Search, ReceiptText, Loader, Lock } from 'lucide-react';
import { LspMatchListSkeleton, LspMarketDetailSkeleton } from '../../../components/Skeleton/Skeleton';

/* ── Helpers ── */
const fmtOdds = (val) => { const n = parseFloat(val); return Number.isFinite(n) ? n.toFixed(2) : '—'; };
const serverOrder = (match) => {
  const n = Number(match?.serverOrder);
  return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
};
const compareServerOrder = (a, b) =>
  (serverOrder(a) - serverOrder(b)) || String(a.id || '').localeCompare(String(b.id || ''));

// Pack consecutive 1-item rows into 2-item rows so unpaired odds share a column
const packRows = (rows) => {
  const packed = [];
  let pending = null;
  for (const row of rows) {
    if (row.length === 1) {
      if (pending) { packed.push([pending[0], row[0]]); pending = null; }
      else { pending = row; }
    } else {
      if (pending) { packed.push(pending); pending = null; }
      packed.push(row);
    }
  }
  if (pending) packed.push(pending);
  return packed;
};

// Derive display state for a single mainOdds slot
const slotState = (slot, fixture, nowMs) => {
  if (!slot?.oddStateId) return 'MISSING';
  if (hasSportsMatchStartElapsed(fixture, nowMs)) return 'LOCKED';
  if (isOddLocked(slot)) return 'LOCKED';
  return 'AVAILABLE';
};
// Fixed Asia/Seoul formatting — never the browser's local timezone.
const fmtMatchTime = (fixture) => formatSportsMatchTime(fixture);
const getSportEmoji = (sport) => {
  const s = (sport || '').toLowerCase();
  if (s.includes('soccer') || s.includes('football') || s.includes('축구')) return '⚽';
  if (s.includes('basketball') || s.includes('농구')) return '🏀';
  if (s.includes('baseball') || s.includes('야구')) return '⚾';
  if (s.includes('tennis') || s.includes('테니스')) return '🎾';
  if (s.includes('hockey') || s.includes('하키')) return '🏒';
  if (s.includes('volleyball') || s.includes('배구')) {
    return <img src={volleyballIcon} alt="" className="lsp_league_sport_icon" />;
  }
  if (s.includes('esports') || s.includes('이스포츠')) return '🎮';
  return '🏆';
};


/* ── Sport tab data ── */
const SPORT_TABS = [
  { id: 'soccer',     icon: soccerIcon,     label: '축구' },
  { id: 'basketball', icon: basketballIcon, label: '농구' },
  { id: 'baseball',   icon: baseballIcon,   label: '야구' },
  { id: 'ice-hockey', icon: hockeyIcon,     label: '아이스 하키' },
  { id: 'volleyball', icon: volleyballIcon, label: '배구' },
];

const SPORT_ICON_BY_SLUG = {
  soccer: soccerIcon,
  basketball: basketballIcon,
  baseball: baseballIcon,
  'ice-hockey': hockeyIcon,
  volleyball: volleyballIcon,
};
const SPORT_LABEL_BY_SLUG = Object.fromEntries(SPORT_TABS.map((tab) => [tab.id, tab.label]));
const sportVisibleInScope = (category, scope) => {
  if (!category || category.active === false) return false;
  if (category.slug === 'esports') return false;
  if (scope === 'domestic') return category.domesticEnabled !== false;
  if (scope === 'overseas') return category.overseasEnabled !== false;
  return true;
};
const sportTabsFromCategories = (categories, scope) => {
  const rows = Array.isArray(categories)
    ? categories.filter((category) => sportVisibleInScope(category, scope))
    : [];
  if (rows.length === 0) return SPORT_TABS;
  return rows.map((category) => ({
    id: category.slug,
    icon: SPORT_ICON_BY_SLUG[category.slug] || soccerIcon,
    label: category.nameKo || category.name || SPORT_LABEL_BY_SLUG[category.slug] || category.slug,
  }));
};

// Slider images definition outside the component
const SLIDER_IMAGES = [
  slide1,
  slide2,
  slide3,
];

/* ════════════════════════════════════════
   MAIN COMPONENT
   ════════════════════════════════════════ */
const InternationalPage = ({ mobileCartOpen = false, onToggleMobileCart = () => {} }) => {
  const { selections, toggleSelection, isSelected } = useContext(SportsBetCartContext);
  const {
    categories,
    fixtures,
    loading,
    listStatus,
    listReady,
    error: listError,
    mode,
    setMode,
    setDisplayScope,
    selectedSport,
    setSelectedSport,
    setSelectedLeagueId,
    detailFixture,
    detailMarkets,
    detailLoading,
    loadFixtureDetail,
    clearFixtureDetail,
    oddsFlash,
    page,
    totalPages,
    goToPage,
    fetchFixtures,
    leagueCountryMap,
  } = useContext(SportsnewContext);

  const [detailOpen, setDetailOpen] = useState(false);
  const [activeSport, setActiveSport]     = useState(''); // '' = all sports
  const [activeMatch, setActiveMatch]     = useState(null);
  const [activeMainTab, setActiveMainTab] = useState('matchup');
  const [activeMktTab, setActiveMktTab]   = useState('all');
  const [sortOrder, setSortOrder]         = useState('time');
  const [nowMs, setNowMs] = useState(() => Date.now());

  // searchInput = raw text as the user types it (bound to the field, updates live).
  // searchVal   = the applied filter — only updated when the search is submitted
  // (▶ click or Enter), so results don't change while typing.
  const [searchInput, setSearchInput] = useState('');
  const [searchVal, setSearchVal] = useState('');
  const searchInputRef = useRef(null);
  const submitSearch = () => { setSearchVal(searchInput); searchInputRef.current?.blur(); };
  const [sliderIndex, setSliderIndex] = useState(0);
  const [selectedCardOdds, setSelectedCardOdds] = useState({});
  const toggleCardOdds = (id) => {
    setSelectedCardOdds(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Set prematch mode and clear any sport filter left by DomesticPage on mount
  useEffect(() => {
    setMode('prematch');
    setDisplayScope('overseas');
    setSelectedSport(null);
    setActiveSport('');
  }, [setMode, setDisplayScope, setSelectedSport]);

  // Autoplay effect
  useEffect(() => {
    const interval = setInterval(() => {
      setSliderIndex(prev => (prev + 1) % SLIDER_IMAGES.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const timerId = window.setInterval(() => setNowMs(Date.now()), 10000);
    return () => window.clearInterval(timerId);
  }, []);

  // Sync activeSport tab with context (sidebar sport click drives this)
  useEffect(() => {
    const slug = selectedSport?.slug ?? '';
    if (slug !== activeSport) setActiveSport(slug);
  }, [selectedSport]); // eslint-disable-line react-hooks/exhaustive-deps

  // Normalise a fixture's sport slug to match SPORT_TABS ids
  const normaliseSportSlug = (f) => {
    const s = (typeof f.sport === 'string' ? f.sport : (f.sport?.slug ?? f.sport?.name ?? '')).toLowerCase().replace(/[-_\s]/g, '');
    if (s.includes('soccer') || s.includes('football') || s.includes('축구')) return 'soccer';
    if (s.includes('basketball') || s.includes('농구')) return 'basketball';
    if (s.includes('baseball') || s.includes('야구')) return 'baseball';
    if (s.includes('icehockey') || s.includes('hockey') || s.includes('하키')) return 'ice-hockey';
    if (s.includes('volleyball') || s.includes('배구')) return 'volleyball';
    if (s.includes('esport') || s.includes('이스포츠')) return 'esports';
    return s;
  };

  // Detail-panel sport chip must reflect the ACTIVE MATCH's own sport, not just the
  // top-tab filter — on "전체" (all sports), activeSport is '' and would otherwise
  // always fall back to the first tab (soccer) regardless of which match is shown.
  const sportTabs = useMemo(() => sportTabsFromCategories(categories, 'overseas'), [categories]);
  const activeMatchSportSlug = activeMatch?._raw ? normaliseSportSlug(activeMatch._raw) : null;
  const activeSportTab = sportTabs.find(tab => tab.id === (activeMatchSportSlug || activeSport));
  const activeSportLabel = activeSportTab ? activeSportTab.label : '';

  const handleSportTab = (id) => {
    const next = activeSport === id ? '' : id; // toggle off if already active
    setActiveSport(next);
    const cat = next ? (categories.find((c) => c.slug === next) || { slug: next }) : null;
    setSelectedSport(cat);
    setSelectedLeagueId(null); // clear league filter so the full sport list shows
  };

  // Map real fixtures to a normalised shape for the UI
  const normalisedFixtures = useMemo(() => {
    // Dedup via Map — Map keys are unique by definition, so the output array is guaranteed dedup
    const dedupMap = new Map();
    for (const f of fixtures) {
      const raw = f.fixtureId ?? f.id;
      if (raw == null) continue;
      const key = String(raw);
      if (!dedupMap.has(key)) dedupMap.set(key, f);
    }
    return Array.from(dedupMap.values())
    .filter((f) => {
      if (activeSport && activeSport !== 'all') {
        return normaliseSportSlug(f) === activeSport;
      }
      return true;
    })
    .map((f, index) => {
      const mo = f.mainOdds ?? {};
      const homePrice = mo.home?.priceDecimal;
      const drawPrice = mo.draw?.priceDecimal;
      const awayPrice = mo.away?.priceDecimal;
      const bettable = f.marketState?.bettable !== false;
      // Fixture rows only ever carry a flat leagueId/leagueName — there is
      // no nested `f.league` object (confirmed against real captured list
      // payloads), so `f.league?.country_flag` never resolved to anything.
      // The country name lives in the navigation tree instead, cross-
      // referenced here by leagueId (see leagueCountryMap in
      // SportsnewContext.jsx); the flag itself is derived from that name
      // via countryFlagEmoji, not a backend-provided image.
      const countryName = resolveSportsnewCountryName(f, leagueCountryMap);
      return {
        _raw: f,
        id: String(f.fixtureId ?? f.id),
        serverOrder: f.rowOrder ?? f.row_order ?? index,
        countryName,
        countryFlag: countryFlagEmoji(countryName),
        leagueName: f.leagueName ?? '',
        leagueSport: getSportsnewSportLabel(f),
        dotColor: bettable ? 'green' : 'gray',
        time: fmtMatchTime(f),
        plusN: f.marketCount ?? 0,
        home: f.home ?? '홈팀',
        away: f.away ?? '원정팀',
        homeLogo: f.homeLogo || null,
        awayLogo: f.awayLogo || null,
        homeOdds: homePrice != null ? fmtOdds(homePrice) : null,
        drawOdds: drawPrice != null ? fmtOdds(drawPrice) : null,
        awayOdds: awayPrice != null ? fmtOdds(awayPrice) : null,
        homeBettable: mo.home?.bettable ?? false,
        drawBettable: mo.draw?.bettable ?? false,
        awayBettable: mo.away?.bettable ?? false,
        homeState: slotState(mo.home, f, nowMs),
        drawState: mo.draw ? slotState(mo.draw, f, nowMs) : null,
        awayState: slotState(mo.away, f, nowMs),
        _homeOdd: mo.home?.oddStateId ? { ...mo.home, id: mo.home.oddStateId, oddStateId: mo.home.oddStateId, odds: homePrice, priceDecimal: homePrice, name: mo.home.label ?? 'Home', bettable: mo.home?.bettable ?? true } : null,
        _drawOdd: mo.draw?.oddStateId ? { ...mo.draw, id: mo.draw.oddStateId, oddStateId: mo.draw.oddStateId, odds: drawPrice, priceDecimal: drawPrice, name: mo.draw.label ?? 'Draw', bettable: mo.draw?.bettable ?? true } : null,
        _awayOdd: mo.away?.oddStateId ? { ...mo.away, id: mo.away.oddStateId, oddStateId: mo.away.oddStateId, odds: awayPrice, priceDecimal: awayPrice, name: mo.away.label ?? 'Away', bettable: mo.away?.bettable ?? true } : null,
      };
    });
  }, [fixtures, activeSport, leagueCountryMap, nowMs]);

  // Filter by search
  const filteredMatches = useMemo(() => normalisedFixtures.filter((m) => {
    const searchLower = searchVal.trim().toLowerCase();
    return !searchLower ||
      m.home.toLowerCase().includes(searchLower) ||
      m.away.toLowerCase().includes(searchLower) ||
      m.leagueName.toLowerCase().includes(searchLower);
  }), [normalisedFixtures, searchVal]);

  // Sort matches
  const sortedMatches = useMemo(() => [...filteredMatches].sort((a, b) => {
    if (sortOrder === 'time') return (a.time || '').localeCompare(b.time || '') || compareServerOrder(a, b);
    return (a.leagueName || '').localeCompare(b.leagueName || '') || compareServerOrder(a, b);
  }), [filteredMatches, sortOrder]);
  const listFailed = !listReady && listStatus === SPORTSNEW_LIST_STATUS.ERROR;
  const listPending = !listReady && !listFailed;
  const listEmpty = listReady && sortedMatches.length === 0;

  // Sync activeMatch when list changes. This must hydrate the same fixture id
  // too, not only reselect when ids change: WS patches can update score/status
  // or mainOdds without changing the list composition, and the detail panel
  // reads from activeMatch.
  useEffect(() => {
    setActiveMatch((prev) => {
      if (!sortedMatches.length) return null;
      if (!prev) return sortedMatches[0];
      return sortedMatches.find((m) => m.id === prev.id) ?? null;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedMatches]);

  // Load detail when activeMatch changes
  useEffect(() => {
    if (!activeMatch?.id) { clearFixtureDetail(); return; }
    loadFixtureDetail(activeMatch.id);
  }, [activeMatch?.id, loadFixtureDetail, clearFixtureDetail]);


  // Card odds toggle with real bet cart
  const handleCardOddsClick = (e, fixture, odd) => {
    e.stopPropagation();
    if (!odd || !odd.bettable) return;
    const homeTeam = fixture.home ?? fixture.home_team ?? '';
    const awayTeam = fixture.away ?? fixture.away_team ?? '';
    toggleSelection({
      odds_id: odd.oddStateId ?? odd.id,
      match_id: fixture.fixtureId ?? fixture.id,
      market_id: odd.marketStateId ?? odd.market_id,
      market_group_key: `${fixture.fixtureId ?? fixture.id}:mainOdds`,
      ...sportsnewSelectionIdentity(odd),
      odds: odd.priceDecimal ?? odd.odds ?? odd.price,
      match_name: `${homeTeam} vs ${awayTeam}`,
      market_name: mainOddsResultLabel(fixture.mainOdds),
      selection_name_display: getSelectionLabel(odd, { match: fixture, fallback: odd.label ?? odd.name }),
    });
  };

  // Render a single odds button in the match list with 4-state display
  const renderListOddsBtn = (state, price, oddObj, raw, flashKey) => {
    if (state === 'MISSING') {
      return (
        <button type="button" className="lsp_odds_value lsp_odds_missing" disabled>—</button>
      );
    }
    if (state === 'LOCKED') {
      return (
        <button type="button" className="lsp_odds_value lsp_odds_locked_state" disabled>
          <Lock size={10} strokeWidth={2.5} className="lsp_lock_icon" />
          <span>{price ?? '—'}</span>
        </button>
      );
    }
    // AVAILABLE
    return (
      <button
        type="button"
        className={`lsp_odds_value${isSelected(oddObj?.id) ? ' lsp_odds_value_active' : ''}${flashKey ? ` dp_odds_flash_${flashKey}` : ''}`}
        onClick={(e) => handleCardOddsClick(e, raw, oddObj)}
      >
        {price ?? '—'}
      </button>
    );
  };

  const detailMarketSource = useMemo(() => (
    detailMarkets.length > 0 ? detailMarkets : (detailFixture?.markets ?? [])
  ), [detailMarkets, detailFixture]);

  const marketTabs = useMemo(() => buildSportsnewMarketTabs({
    fixture: detailFixture,
    markets: detailMarketSource,
    sport: activeMatchSportSlug || activeSport,
  }), [detailFixture, detailMarketSource, activeMatchSportSlug, activeSport]);

  useEffect(() => {
    const next = ensureSportsnewMarketTab(marketTabs, activeMktTab);
    if (next !== activeMktTab) setActiveMktTab(next);
  }, [marketTabs, activeMktTab]);

  // Detail panel: filter by active tab, group odds into rows by stable
  // identity (see marketRowSorting.js — never by market/selection name).
  const groupedDetailMarkets = useMemo(() => {
    const filtered = detailMarketSource.filter((market) =>
      marketMatchesSportsnewCategory(market, activeMktTab));

    // The API sends one array entry per LINE, sharing one type identity
    // (marketTypeKey / marketStateId) across e.g. Asian Total Goals'
    // 1.5/2.5/3.5 — merge those back into one logical market before
    // splitting into rows, so every line lands as a ROW under one shared
    // section instead of three separate repeated sections.
    return groupMarketsByType(filtered).map((market) => {
      const odds = market._mergedOdds ?? market.selections ?? market.odds ?? [];
      const rowMap = new Map();
      const rowOrder = [];
      odds.forEach((odd) => {
        const key = deriveGroupKey(odd, market);
        if (!rowMap.has(key)) { rowMap.set(key, []); rowOrder.push(key); }
        rowMap.get(key).push(odd);
      });
      const home = activeMatch?.home;
      const away = activeMatch?.away;
      const rows = packRows(rowOrder.map((k) => sortMarketRow(rowMap.get(k), home, away)));
      // Market titles stay line-free; every row/selection already carries
      // its own line via oddRowLineLabel.
      return { ...market, _rows: rows, _lineLabel: '' };
    });
  }, [detailMarketSource, activeMktTab, activeMatch?.home, activeMatch?.away]);

  // Get dynamic standings and results based on the current match league
  const getLeagueStandingsAndResults = () => {
    const leagueName = activeMatch?.leagueName || '';

    // Default standings data structure
    let standings = [
      { rank: 1, team: activeMatch?.home || '홈팀', played: 15, win: 10, draw: 3, loss: 2, gd: '+12', pts: 33 },
      { rank: 2, team: activeMatch?.away || '원정팀', played: 15, win: 8, draw: 4, loss: 3, gd: '+6', pts: 28 },
      { rank: 3, team: '토트넘', played: 15, win: 7, draw: 5, loss: 3, gd: '+4', pts: 26 },
      { rank: 4, team: '뉴캐슬', played: 15, win: 6, draw: 4, loss: 5, gd: '+1', pts: 22 },
      { rank: 5, team: '아스톤 빌라', played: 15, win: 5, draw: 5, loss: 5, gd: '0', pts: 20 },
      { rank: 6, team: '웨스트햄', played: 15, win: 4, draw: 3, loss: 8, gd: '-8', pts: 15 },
    ];

    let results = [
      { home: activeMatch?.home || '홈팀', score: '2 : 1', away: '토트넘', date: '06/15' },
      { home: '뉴캐슬', score: '0 : 2', away: activeMatch?.away || '원정팀', date: '06/12' },
      { home: activeMatch?.home || '홈팀', score: '1 : 1', away: activeMatch?.away || '원정팀', date: '06/08' },
      { home: '아스톤 빌라', score: '3 : 1', away: '웨스트햄', date: '06/05' }
    ];

    if (leagueName.includes('K리그') || leagueName.includes('K리그 1')) {
      standings = [
        { rank: 1, team: '울산 HD', played: 18, win: 11, draw: 4, loss: 3, gd: '+15', pts: 37 },
        { rank: 2, team: '김천 상무', played: 18, win: 10, draw: 5, loss: 3, gd: '+9', pts: 35 },
        { rank: 3, team: '포항 스틸러스', played: 18, win: 9, draw: 6, loss: 3, gd: '+8', pts: 33 },
        { rank: 4, team: '강원 FC', played: 18, win: 9, draw: 4, loss: 5, gd: '+5', pts: 31 },
        { rank: 5, team: '수원 FC', played: 18, win: 8, draw: 3, loss: 7, gd: '-2', pts: 27 },
        { rank: 6, team: '전북 현대', played: 18, win: 7, draw: 5, loss: 6, gd: '+2', pts: 26 },
      ];
      results = [
        { home: '울산 HD', score: '2 : 1', away: '전북 현대', date: '06/18' },
        { home: '김천 상무', score: '1 : 0', away: '포항 스틸러스', date: '06/15' },
        { home: '강원 FC', score: '3 : 1', away: '수원 FC', date: '06/12' },
        { home: '전북 현대', score: '2 : 2', away: '김천 상무', date: '06/09' }
      ];
    } else if (leagueName.includes('Premier League')) {
      standings = [
        { rank: 1, team: 'Arsenal', played: 38, win: 28, draw: 5, loss: 5, gd: '+62', pts: 89 },
        { rank: 2, team: 'Manchester City', played: 38, win: 28, draw: 4, loss: 6, gd: '+58', pts: 88 },
        { rank: 3, team: 'Liverpool', played: 38, win: 24, draw: 10, loss: 4, gd: '+45', pts: 82 },
        { rank: 4, team: 'Aston Villa', played: 38, win: 20, draw: 8, loss: 10, gd: '+15', pts: 68 },
        { rank: 5, team: 'Tottenham Hotspur', played: 38, win: 20, draw: 6, loss: 12, gd: '+13', pts: 66 },
        { rank: 6, team: 'Chelsea', played: 38, win: 18, draw: 9, loss: 11, gd: '+14', pts: 63 },
      ];
      results = [
        { home: 'Arsenal', score: '2 : 1', away: 'Everton', date: '05/19' },
        { home: 'Manchester City', score: '3 : 1', away: 'West Ham', date: '05/19' },
        { home: 'Chelsea', score: '2 : 1', away: 'Bournemouth', date: '05/19' },
        { home: 'Liverpool', score: '2 : 0', away: 'Wolves', date: '05/19' }
      ];
    } else if (leagueName.includes('월드컵')) {
      standings = [
        { rank: 1, team: '네덜란드', played: 3, win: 2, draw: 1, loss: 0, gd: '+4', pts: 7 },
        { rank: 2, team: '스웨덴', played: 3, win: 1, draw: 2, loss: 0, gd: '+2', pts: 5 },
        { rank: 3, team: '프랑스', played: 3, win: 1, draw: 1, loss: 1, gd: '0', pts: 4 },
        { rank: 4, team: '칠레', played: 3, win: 0, draw: 0, loss: 3, gd: '-6', pts: 0 },
      ];
      results = [
        { home: '네덜란드', score: '1 : 1', away: '스웨덴', date: '06/21' },
        { home: '프랑스', score: '2 : 0', away: '칠레', date: '06/18' },
        { home: '네덜란드', score: '2 : 1', away: '프랑스', date: '06/14' },
        { home: '스웨덴', score: '3 : 1', away: '칠레', date: '06/13' }
      ];
    }

    return { standings, results };
  };

  return (
    <div className="lsp_page">
      {/* ── Two-Panel Shell ── */}
      <div className="lsp_shell">
        <div className="lsp_sheller">
          <div className="">
            {/* ── Hero Slider (autoplay, no controls) ── */}
            <div className="lsp_hero">
              <div
                className="lsp_hero_track"
                style={{ transform: `translateX(-${sliderIndex * 100}%)` }}
              >
                {SLIDER_IMAGES.map((img, idx) => (
                  <img key={idx} src={img} alt={`slide-${idx}`} className="lsp_hero_img" />
                ))}
              </div>
            </div>
          </div>

          {/* ── Sport Tabs ── */}
          <div className="lsp_sport_tabs">
            {sportTabs.map(tab => (
              <button
                key={tab.id}
                type="button"
                className={`lsp_sport_tab ${activeSport === tab.id ? 'lsp_sport_tab_active' : ''}`}
                onClick={() => handleSportTab(tab.id)}
              >
                <img src={tab.icon} alt={tab.label} className="lsp_sport_svg_icon" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>

          {/* ── Search Bar ── */}
          <div className="lsp_search_row">
            <span className="lsp_search_icon"><Search size={18} /></span>
            <input
              ref={searchInputRef}
              className="lsp_search_input"
              placeholder="팀명 또는 리그명을 입력해주세요."
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') submitSearch(); }}
            />
            <div className="lsp_search_actions">
              <button type="button" className="lsp_search_btn_icon" onClick={submitSearch}>▶</button>
              <button type="button" className="lsp_search_btn_icon" onClick={() => { setSearchInput(''); setSearchVal(''); searchInputRef.current?.focus(); }}>✕</button>
            </div>
          </div>

          {/* ── Sort Toolbar ── */}
          <div className="lsp_sort_row">
            <button
              type="button"
              className={`lsp_sort_btn ${sortOrder === 'time' ? 'lsp_sort_btn_active' : ''}`}
              onClick={() => setSortOrder('time')}
            >
              <ArrowDownUp size={14} /> 시간별정렬
            </button>
            <button
              type="button"
              className={`lsp_sort_btn ${sortOrder === 'league' ? 'lsp_sort_btn_active' : ''}`}
              onClick={() => setSortOrder('league')}
            >
              <ArrowUpDown size={14} /> 리그별정렬
            </button>
            <div className="lsp_sort_spacer" />
            <button type="button" className="lsp_sort_refresh" disabled={loading} onClick={() => fetchFixtures()}><RefreshCw size={14} /></button>
          </div>
        </div>

        {/* ═══ LEFT: Match List ═══ */}
        <div className="lsp_list">
          <div className="lsp_list_scroll">
            {listPending && <LspMatchListSkeleton count={5} />}
            {listFailed && (
              <div className="lsp_empty" role="alert">
                <span>{listError || '경기 목록을 불러오지 못했습니다.'}</span>
                <button type="button" onClick={() => fetchFixtures()} disabled={loading}>다시 시도</button>
              </div>
            )}
            {listEmpty && (
              <div className="lsp_empty">진행 중인 경기가 없습니다.</div>
            )}
            {listReady && sortedMatches.map((m) => (
              <div key={m.id} className={`lsp_match_group ${activeMatch?.id === m.id ? 'lsp_match_group_active' : ''}`}>
                {/* League header */}
                <div className="lsp_league_header">
                  <span className="lsp_league_flag_emoji" role="img" aria-label={m.countryName || '국제'}>{m.countryFlag}</span>
                  <span className="lsp_league_name">{m.countryName ? `${m.countryName} - ${m.leagueName}` : m.leagueName}</span>
                  <span className="lsp_league_badge">
                    {getSportEmoji(m.leagueSport)} {m.leagueSport}
                  </span>
                  <span className="lsp_league_time">{m.time}</span>
                  {/* <span className="lsp_league_stats">
                    <ChartNoAxesColumn size={15} style={{ color: 'var(--lsp-pink)' }} />
                  </span> */}
                  <button
                    type="button"
                    className="lsp_more_badge"
                    onClick={(e) => { e.stopPropagation(); setActiveMatch(m); setDetailOpen(true); }}
                  >
                    +{m.plusN}
                  </button>
                </div>

                {/* Match row */}
                <div
                  className={`lsp_match_row ${activeMatch?.id === m.id ? 'lsp_match_row_active' : ''}`}
                  onClick={() => setActiveMatch(m)}
                >
                  {/* Teams */}
                  <div className="lsp_teams_col">
                    <div className="lsp_team_row">
                      <img src={m.homeLogo || dressRedImage} className="lsp_team_jersey" alt="Home" />
                      <span className="lsp_team_name">{m.home}</span>
                    </div>
                    <div className="lsp_team_row">
                      <img src={m.awayLogo || dressBlueImage} className="lsp_team_jersey" alt="Away" />
                      <span className="lsp_team_name">{m.away}</span>
                    </div>
                  </div>

                  {/* Odds columns */}
                  <div className="lsp_odds_cols">
                    <div className="lsp_odds_col">
                      <span className="lsp_odds_label lsp_odds_label_home">Home</span>
                      {renderListOddsBtn(m.homeState, m.homeOdds, m._homeOdd, m._raw, oddsFlash[m._homeOdd?.id])}
                    </div>
                    {m.drawState != null ? (
                      <div className="lsp_odds_col">
                        <span className="lsp_odds_label lsp_odds_label_draw">Draw</span>
                        {renderListOddsBtn(m.drawState, m.drawOdds, m._drawOdd, m._raw, null)}
                      </div>
                    ) : (
                      <div className="lsp_odds_col lsp_odds_col_vs">
                        <span className="lsp_vs_label">vs</span>
                      </div>
                    )}
                    <div className="lsp_odds_col">
                      <span className="lsp_odds_label lsp_odds_label_away">Away</span>
                      {renderListOddsBtn(m.awayState, m.awayOdds, m._awayOdd, m._raw, oddsFlash[m._awayOdd?.id])}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="lsp_pagination">
              <button
                className="lsp_page_btn"
                disabled={page <= 1}
                onClick={() => goToPage(page - 1)}
              >‹</button>
              <span className="lsp_page_info">{page} / {totalPages}</span>
              <button
                className="lsp_page_btn"
                disabled={page >= totalPages}
                onClick={() => goToPage(page + 1)}
              >›</button>
            </div>
          )}
        </div>
      </div>

      {/* ═══ RIGHT: Detail Panel ═══ */}
      {listReady && activeMatch && (
        <SportsbookMobilePortal open={listReady && detailOpen} inactive={mobileCartOpen} breakpoint={900} className="zxsb_mobile_detail_portal">
        <div className={`lsp_detail ${detailOpen ? 'lsp_detail_mobile_open' : ''}`}>
          {/* Close button for mobile drawer */}
          <button
            type="button"
            className="lsp_detail_close_btn"
            onClick={() => setDetailOpen(false)}
          >
            ✕ 닫기
          </button>

          <button type="button" className="lsp_detail_cart_btn" onClick={onToggleMobileCart}>
            베팅카트 ({selections.length})
          </button>

          {/* Top nav breadcrumb */}
          <div className="lsp_detail_topnav">
            <div className="lsp_detail_sport_chip">
              <img src={activeSportTab?.icon || soccerIcon} width={17} alt="SportIcon" />
              {activeSportLabel}
            </div>
            <span className="lsp_detail_nav_sep">/</span>
            <div className="lsp_detail_league_chip">
              <span className="lsp_league_flag_emoji" role="img" aria-label={activeMatch.countryName || '국제'}>{activeMatch.countryFlag}</span>
              {activeMatch.countryName ? `${activeMatch.countryName} - ${activeMatch.leagueName}` : activeMatch.leagueName}
            </div>
          </div>

          {/* Match header */}
          <div className="lsp_match_header">
            <div className="lsp_match_usersbox">
              <img width={30} src={activeMatch?.homeLogo} alt="Dress" />
              <span>{activeMatch.home}</span>
            </div>

            <div className="lsp_match_timebox">
              <span>{(activeMatch.time || '').split(' ')[0]}</span>
              <span> | </span>
              <span>{(activeMatch.time || '').split(' ')[1]}</span>
            </div>

            <div className="lsp_match_usersbox">
              <span>{activeMatch.away}</span>
              <img width={30} src={activeMatch?.awayLogo} alt="Dress" />
            </div>
          </div>

          <div className="">

          </div>

          {/* Main tabs: 맞대결 / 순위 및 결과
          <div className="lsp_detail_main_tabs">
            <button
              type="button"
              className={`lsp_detail_main_tab ${activeMainTab === 'matchup' ? 'lsp_detail_main_tab_active' : ''}`}
              onClick={() => setActiveMainTab('matchup')}
            >
              <span className="lsp_detail_main_tab_icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="lsp_tab_svg">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </span>
              맞대결
            </button>
            <button
              type="button"
              className={`lsp_detail_main_tab ${activeMainTab === 'ranking' ? 'lsp_detail_main_tab_active' : ''}`}
              onClick={() => setActiveMainTab('ranking')}
            >
              <span className="lsp_detail_main_tab_icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="lsp_tab_svg">
                  <rect x="3" y="4" width="18" height="4" rx="1" />
                  <rect x="3" y="10" width="18" height="4" rx="1" />
                  <rect x="3" y="16" width="18" height="4" rx="1" />
                  <line x1="8" y1="4" x2="8" y2="8" />
                  <line x1="8" y1="10" x2="8" y2="14" />
                  <line x1="8" y1="16" x2="8" y2="20" />
                </svg>
              </span>
              순위 및 결과
            </button>
          </div>*/}

          {/* Market sub-tabs - Only shown for matchup */}
          {activeMainTab === 'matchup' && (
            <div className="lsp_mkt_tabs">
              {marketTabs.map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  className={`lsp_mkt_tab ${activeMktTab === tab.id ? 'lsp_mkt_tab_active' : ''}`}
                  onClick={() => setActiveMktTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          )}

          {/* Scrollable Detail Area */}
          <div className="lsp_detail_scroll">
            {activeMainTab === 'matchup' ? (
              <>
                {/* Real markets from API */}
                {detailLoading && <LspMarketDetailSkeleton count={5} />}

                {!detailLoading && groupedDetailMarkets.length > 0 && (
                  groupedDetailMarkets.map((market) => {
                    if (!market.bettable && market.suspended) return null;
                    // A single marketStateId can be reused across multiple
                    // DISTINCT markets (different lines/periods/team-scoped
                    // variants) — the section key (and the betslip's
                    // market_group_key below) must use the fuller identity
                    // (marketGroupId first, else a composite fallback), or
                    // two such markets collide and only one renders/selects.
                    const groupKey = marketIdentityKey(market) ?? market.marketStateId ?? market.name;
                    return (
                      <div key={groupKey} className="lsp_mkt_section">
                        <div className="lsp_mkt_section_header">{market.name}{market._lineLabel}</div>
                        {market._rows.map((row) => {
                          const colCount = row.length === 3 ? 3 : 2;
                          // A merged multi-line section holds several distinct
                          // lines under one groupKey now — the betslip's
                          // exclusivity key must stay scoped to THIS row's own
                          // line (via deriveGroupKey, the same identity the
                          // rows themselves were split by), or selecting
                          // "Over 2.5" and "Over 1.5" together would wrongly
                          // collide as if they were the same market.
                          const rowGroupKey = `${groupKey}:${deriveGroupKey(row[0], market)}`;
                          return (
                            <div key={rowGroupKey} className={colCount === 3 ? 'lsp_grid_row_3' : 'lsp_grid_row'}>
                              {row.map((odd) => {
                                const isLocked  = isOddLocked(odd);
                                const isMissing = !isLocked && (!odd.oddStateId || odd.priceDecimal == null);
                                const cellState = isMissing ? 'MISSING' : isLocked ? 'LOCKED' : 'AVAILABLE';
                                const oddLabel = getSelectionLabel(odd, { market, match: activeMatch });
                                const oddKey = odd.oddStateId ?? `${rowGroupKey}:${odd.name ?? oddLabel}`;
                                return (
                                <button
                                  key={oddKey}
                                  type="button"
                                  className={`lsp_grid_cell${isSelected(odd.oddStateId) ? ' lsp_grid_cell_active' : ''}${cellState === 'LOCKED' ? ' dp_cell_locked' : ''}${cellState === 'MISSING' ? ' dp_cell_missing' : ''}${oddsFlash[odd.oddStateId] ? ` dp_odds_flash_${oddsFlash[odd.oddStateId]}` : ''}`}
                                  disabled={cellState !== 'AVAILABLE'}
                                  onClick={() => {
                                    if (cellState !== 'AVAILABLE') return;
                                    toggleSelection({
                                      odds_id: odd.oddStateId,
                                      match_id: activeMatch.id,
                                      market_id: market.marketStateId,
                                      market_group_key: `${activeMatch.id}:${rowGroupKey}`,
                                      ...sportsnewSelectionIdentity(odd),
                                      odds: odd.priceDecimal,
                                      match_name: `${activeMatch.home} vs ${activeMatch.away}`,
                                      market_name: market.name,
                                      selection_name_display: oddLabel,
                                    });
                                  }}
                                >
                                  <span className="lsp_grid_cell_label">{oddLabel}{oddRowLineLabel(odd, market)}</span>
                                  {cellState === 'LOCKED'
                                    ? <span className="dp_cell_locked_odds"><Lock size={10} strokeWidth={2.5} />{fmtOdds(odd.priceDecimal)}</span>
                                    : <span className="lsp_grid_cell_odds">{cellState === 'MISSING' ? '—' : fmtOdds(odd.priceDecimal)}</span>
                                  }
                                </button>
                                );
                              })}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })
                )}

                {!detailLoading && groupedDetailMarkets.length === 0 && (
                  <div className="lsp_no_markets">배당 정보가 없습니다.</div>
                )}

              </>
            ) : (
              /* Standings & Results */
              <div className="lsp_standings_container">
                <div className="lsp_mkt_section_header">리그 순위 (Standings)</div>
                <table className="lsp_standings_table">
                  <thead>
                    <tr>
                      <th style={{ width: '50px' }}>순위</th>
                      <th>팀명</th>
                      <th>경기</th>
                      <th>승</th>
                      <th>무</th>
                      <th>패</th>
                      <th>득실</th>
                      <th>승점</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getLeagueStandingsAndResults().standings.map((team, idx) => (
                      <tr key={idx}>
                        <td>
                          <span className={`lsp_rank_badge ${idx === 0 ? 'lsp_rank_badge_pink' : idx === 1 ? 'lsp_rank_badge_blue' : 'lsp_rank_badge_gray'}`}>
                            {team.rank}
                          </span>
                        </td>
                        <td>{team.team}</td>
                        <td>{team.played}</td>
                        <td>{team.win}</td>
                        <td>{team.draw}</td>
                        <td>{team.loss}</td>
                        <td>{team.gd}</td>
                        <td style={{ fontWeight: 700, color: 'var(--lsp-pink)' }}>{team.pts}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="lsp_mkt_section_header" style={{ marginTop: '10px' }}>최근 경기 결과 (Recent Results)</div>
                <div className="lsp_recent_results">
                  {getLeagueStandingsAndResults().results.map((res, idx) => (
                    <div className="lsp_result_item" key={idx}>
                      <span className="lsp_result_team">{res.home}</span>
                      <span className="lsp_result_score">{res.score}</span>
                      <span className="lsp_result_team lsp_result_team_right">{res.away}</span>
                      <span className="lsp_result_date">{res.date}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        </SportsbookMobilePortal>
      )}

      {/* Floating betslip button for mobile view */}
      <button
        type="button"
        className="ipp_mobile_cart_fab"
        onClick={onToggleMobileCart}
      >
        <span className="ipp_mobile_cart_icon_wrap">
          <ReceiptText size={18} />
          {selections.length > 0 && (
            <span className="ipp_mobile_cart_badge">{selections.length}</span>
          )}
        </span>
        <span>베팅카트</span>
      </button>

      {/* Scrim overlay for details drawer */}
      {listReady && detailOpen && (
        <div
          className="lsp_detail_scrim"
          onClick={() => setDetailOpen(false)}
        />
      )}

    </div>
  );
};

export default InternationalPage;
